import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import Autocomplete from './ui/autocomplete';
import { Progress } from './ui/progress';
import { 
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  Users, 
  Calendar, 
  ClipboardList, 
  BarChart3,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Star,
  TrendingUp,
  FileText,
  Settings,
  Plus,
  Edit,
  Eye,
  Download,
  Brain,
  Lightbulb,
  ArrowRight,
  PlayCircle,
  PauseCircle,
  Award,
  UserCheck,
  Gauge,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AvaliacaoDesempenhoAvancada = () => {
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState('metas-avancadas');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [ciclos, setCiclos] = useState([]);
  const [cicloAtivo, setCicloAtivo] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [metricas, setMetricas] = useState({});
  
  // Estados para filtros da aba Avaliações
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroDepartamento, setFiltroDepartamento] = useState('todos');
  
  // Estados para aba Resultados
  const [resultadosConsolidados, setResultadosConsolidados] = useState(null);
  const [insightsIA, setInsightsIA] = useState(null);
  const [loadingResultados, setLoadingResultados] = useState(false);
  
  // Estados para Análise IA de Avaliação Individual
  const [showAnaliseIAModal, setShowAnaliseIAModal] = useState(false);
  const [analiseIAAtual, setAnaliseIAAtual] = useState(null);
  const [loadingAnaliseIA, setLoadingAnaliseIA] = useState(false);
  
  // Estados para Metas Avançadas
  const [metas, setMetas] = useState([]);
  const [metasSelecionadas, setMetasSelecionadas] = useState([]);
  const [dashboardMetas, setDashboardMetas] = useState({});
  const [calculadoraMeta, setCalculadoraMeta] = useState({
    valor_inicial: 0,
    valor_meta: 0,
    valor_obtido: 0,
    tipo_meta: 'crescimento'
  });
  
  // Form states
  const [novoCiclo, setNovoCiclo] = useState({
    nome: '',
    descricao: '',
    data_inicio: '',
    data_fim: '',
    prazo_avaliacao: '',
    prazo_aprovacao: '',
    tipos_avaliacao: [],
    peso_metas: 60,
    peso_competencias: 40,
    peso_gestor: 70,
    peso_autoavaliacao: 30
  });
  
  const [novaAvaliacao, setNovaAvaliacao] = useState({
    ciclo_id: '',
    colaborador_id: '',
    tipo_avaliador: 'auto',
    respostas: [],
    pontos_fortes: '',
    areas_desenvolvimento: '',
    feedback_geral: ''
  });
  
  const [novaMeta, setNovaMeta] = useState({
    nome: '',
    descricao: '',
    indicador: '',
    unidade_medida: 'unidades',
    valor_inicial: 0,
    valor_meta: 0,
    peso: 1.0,
    tipo_meta: 'crescimento',
    periodo_inicio: '',
    periodo_fim: '',
    colaborador_id: '',
    area: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);


  useEffect(() => {
    if (currentTab === 'resultados') {
      loadResultadosConsolidados();
      loadInsightsIA();
    }
  }, [currentTab]);


  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadColaboradores(),
        loadMetricas(),
        loadAvaliacoesAvancadas()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados iniciais');
    } finally {
      setLoading(false);
    }
  };

  const loadCiclos = async () => {
    try {
      const response = await axios.get(`${API}/api/avaliacao-desempenho/ciclos`);
      setCiclos(response.data);
      
      // Encontrar ciclo ativo
      const ativo = response.data.find(c => c.status === 'ativo');
      if (ativo) {
        setCicloAtivo(ativo);
        await loadResultadosCiclo(ativo.id);
      }
    } catch (error) {
      console.error('Erro ao carregar ciclos:', error);
      toast.error('Erro ao carregar ciclos de avaliação');
    }
  };

  const loadColaboradores = async () => {
    try {
      const response = await axios.get(`${API}/api/employees`);
      setColaboradores(response.data);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
    }
  };

  const loadAvaliacoesAvancadas = async (statusFilter = 'todos', departamentoFilter = 'todos') => {
    try {
      let url = `${API}/api/avaliacao-desempenho-avancada/avaliacoes`;
      const params = [];
      
      if (statusFilter && statusFilter !== 'todos') {
        params.push(`status_filter=${statusFilter}`);
      }
      
      if (departamentoFilter && departamentoFilter !== 'todos') {
        params.push(`departamento_filter=${encodeURIComponent(departamentoFilter)}`);
      }
      
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      
      const response = await axios.get(url);
      setAvaliacoes(response.data.avaliacoes || []);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
    }
  };


  const loadResultadosConsolidados = async () => {
    try {
      setLoadingResultados(true);
      const response = await axios.get(`${API}/api/avaliacao-desempenho-avancada/resultados/consolidado`);
      setResultadosConsolidados(response.data);
    } catch (error) {
      console.error('Erro ao carregar resultados consolidados:', error);
      toast.error('Erro ao carregar resultados consolidados');
    } finally {
      setLoadingResultados(false);
    }
  };

  const loadInsightsIA = async () => {
    try {
      const response = await axios.get(`${API}/api/avaliacao-desempenho-avancada/resultados/insights-ia`);
      setInsightsIA(response.data);
    } catch (error) {
      console.error('Erro ao carregar insights de IA:', error);
      toast.error('Erro ao carregar insights de IA');
    }
  };

  const exportarPDF = async () => {
    try {
      toast.info('Gerando PDF...');
      const response = await axios.get(`${API}/api/avaliacao-desempenho-avancada/resultados/exportar/pdf`, {
        responseType: 'blob'
      });
      
      // Criar URL para o blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Criar link temporário e clicar
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio_desempenho_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  const exportarExcel = async () => {
    try {
      toast.info('Gerando Excel...');
      const response = await axios.get(`${API}/api/avaliacao-desempenho-avancada/resultados/exportar/excel`, {
        responseType: 'blob'
      });
      
      // Criar URL para o blob
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      
      // Criar link temporário e clicar
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio_desempenho_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao gerar Excel');
    }
  };


  const gerarAnaliseIAAvaliacao = async (avaliacao) => {
    try {
      setLoadingAnaliseIA(true);
      setShowAnaliseIAModal(true);
      
      // Buscar metas do colaborador
      const metasResponse = await axios.get(`${API}/api/avaliacao-desempenho/metas/dashboard/${avaliacao.colaborador_id}`);
      const metas = metasResponse.data.metas_detalhadas || [];
      
      // Preparar dados para análise
      const metasComResultado = metas.filter(m => m.valor_obtido !== null);
      const totalMetas = metasComResultado.length;
      const metasSuperou = metasComResultado.filter(m => m.classificacao === 'superou').length;
      const metasAtingiu = metasComResultado.filter(m => m.classificacao === 'atingiu').length;
      const metasNaoAtingiu = metasComResultado.filter(m => m.classificacao === 'nao_atingiu').length;
      
      // Gerar análise com IA
      const prompt = `Analise o desempenho do colaborador ${avaliacao.colaborador_nome}:

Métricas:
- Total de metas: ${totalMetas}
- Metas superadas: ${metasSuperou}
- Metas atingidas: ${metasAtingiu}
- Metas não atingidas: ${metasNaoAtingiu}
- Score médio: ${avaliacao.metricas?.score_medio || 'N/A'}
- Taxa de sucesso: ${avaliacao.metricas?.percentual_medio || 0}%

Metas detalhadas:
${metasComResultado.map(m => `- ${m.nome}: ${m.percentual_real}% (${m.classificacao})`).join('\n')}

Forneça uma análise em JSON com:
{
  "resumo_executivo": "Resumo de 2-3 frases sobre o desempenho geral",
  "pontos_fortes": ["Ponto forte 1", "Ponto forte 2", "Ponto forte 3"],
  "areas_melhoria": ["Área 1", "Área 2"],
  "recomendacoes": ["Recomendação específica 1", "Recomendação 2", "Recomendação 3"],
  "proximos_passos": ["Ação 1", "Ação 2"]
}`;

      // Simular chamada de IA (você pode implementar endpoint real)
      const analise = {
        resumo_executivo: `${avaliacao.colaborador_nome} demonstrou ${avaliacao.metricas?.percentual_medio >= 70 ? 'excelente' : 'bom'} desempenho no período avaliado, com ${metasSuperou} metas superadas de ${totalMetas} totais. O score médio de ${avaliacao.metricas?.score_medio} reflete um profissional ${avaliacao.metricas?.classificacao_geral === 'superou' ? 'acima das expectativas' : 'alinhado com as expectativas'}.`,
        pontos_fortes: [
          `Atingiu ${avaliacao.metricas?.percentual_medio}% de taxa de sucesso nas metas`,
          metasSuperou > 0 ? `Superou ${metasSuperou} meta${metasSuperou > 1 ? 's' : ''} além do esperado` : 'Demonstrou comprometimento com os objetivos',
          `Score médio de ${avaliacao.metricas?.score_medio} indica performance consistente`
        ],
        areas_melhoria: metasNaoAtingiu > 0 ? [
          `${metasNaoAtingiu} meta${metasNaoAtingiu > 1 ? 's não foram atingidas' : ' não foi atingida'} - requer atenção`,
          'Oportunidade de melhoria no planejamento estratégico'
        ] : [
          'Manter consistência na performance',
          'Buscar desafios mais ambiciosos'
        ],
        recomendacoes: [
          avaliacao.metricas?.classificacao_geral === 'superou' ? 'Considerar promoção ou aumento de responsabilidades' : 'Fornecer mentoria e suporte adicional',
          'Estabelecer plano de desenvolvimento individual (PDI)',
          metasNaoAtingiu > 0 ? 'Revisar processos que impactaram metas não atingidas' : 'Reconhecer e recompensar os resultados alcançados'
        ],
        proximos_passos: [
          'Agendar reunião 1:1 para feedback detalhado',
          'Definir novas metas para o próximo período',
          'Acompanhar progresso mensalmente'
        ]
      };
      
      setAnaliseIAAtual({
        colaborador: avaliacao.colaborador_nome,
        cargo: avaliacao.colaborador_cargo,
        departamento: avaliacao.colaborador_departamento,
        periodo: `${new Date(avaliacao.periodo_inicio).toLocaleDateString()} - ${new Date(avaliacao.periodo_fim).toLocaleDateString()}`,
        metricas: avaliacao.metricas,
        analise: analise
      });
      
    } catch (error) {
      console.error('Erro ao gerar análise IA:', error);
      toast.error('Erro ao gerar análise de IA');
    } finally {
      setLoadingAnaliseIA(false);
    }
  };


  const exportarAnaliseIA = async () => {
    try {
      if (!analiseIAAtual) {
        toast.error('Nenhuma análise para exportar');
        return;
      }

      toast.info('Gerando PDF da análise...');
      
      const response = await axios.post(
        `${API}/api/avaliacao-desempenho-avancada/avaliacoes/exportar-analise-ia`,
        analiseIAAtual,
        { responseType: 'blob' }
      );
      
      // Criar URL para o blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Criar link temporário e clicar
      const link = document.createElement('a');
      link.href = url;
      const nomeColaborador = analiseIAAtual.colaborador.replace(/\s+/g, '_');
      link.download = `analise_ia_${nomeColaborador}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Análise exportada em PDF com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar análise IA:', error);
      toast.error('Erro ao exportar análise em PDF');
    }
  };


  const concluirAvaliacao = async (avaliacaoId) => {
    try {
      const confirmacao = window.confirm('Tem certeza que deseja marcar esta avaliação como concluída?');
      
      if (!confirmacao) return;
      
      await axios.put(`${API}/api/avaliacao-desempenho-avancada/avaliacoes/${avaliacaoId}/concluir`);
      
      toast.success('Avaliação marcada como concluída!');
      
      // Recarregar lista de avaliações
      loadAvaliacoesAvancadas(filtroStatus, filtroDepartamento);
      
      // Recarregar métricas do dashboard
      loadMetricas();
      
    } catch (error) {
      console.error('Erro ao concluir avaliação:', error);
      toast.error('Erro ao concluir avaliação');
    }
  };

  const cancelarAvaliacao = async (avaliacaoId) => {
    try {
      const confirmacao = window.confirm('Tem certeza que deseja cancelar esta avaliação? Esta ação não pode ser desfeita.');
      
      if (!confirmacao) return;
      
      await axios.put(
        `${API}/api/avaliacao-desempenho-avancada/avaliacoes/${avaliacaoId}/cancelar`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      toast.success('Avaliação cancelada com sucesso!');
      
      loadAvaliacoesAvancadas(filtroStatus, filtroDepartamento);
      loadMetricas();
      
    } catch (error) {
      console.error('Erro ao cancelar avaliação:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao cancelar avaliação';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erro ao cancelar avaliação');
    }
  };




  const loadMetricas = async () => {
    try {
      // Buscar métricas do dashboard do módulo avançado (não vincular a ciclos básicos)
      const response = await axios.get(`${API}/api/avaliacao-desempenho-avancada/dashboard/metricas`);
      const data = response.data;
      
      setMetricas({
        total_colaboradores: data.total_colaboradores || 0,
        avaliacoes_pendentes: data.avaliacoes_pendentes || 0,
        avaliacoes_concluidas: data.avaliacoes_concluidas || 0,
        taxa_conclusao: data.taxa_conclusao || 0
      });
      
      // Atualizar resultados recentes com análise de IA
      if (data.resultados_recentes && data.resultados_recentes.length > 0) {
        setResultados(data.resultados_recentes);
      }
    } catch (error) {
      console.error('Erro ao carregar métricas:', error);
      toast.error('Erro ao carregar métricas do dashboard');
    }
  };

  // ============ FUNÇÕES PARA METAS AVANÇADAS ============

  const loadMetasColaborador = async (colaboradorId) => {
    try {
      const response = await axios.get(`${API}/api/avaliacao-desempenho/metas/${colaboradorId}`);
      setMetas(response.data);
    } catch (error) {
      console.error('Erro ao carregar metas:', error);
      toast.error('Erro ao carregar metas do colaborador');
    }
  };

  const loadDashboardMetas = async (colaboradorId) => {
    try {
      const response = await axios.get(`${API}/api/avaliacao-desempenho/metas/dashboard/${colaboradorId}`);
      setDashboardMetas(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard de metas:', error);
    }
  };

  const handleCreateMeta = async (e) => {
    e.preventDefault();

    const valorInicial = Number(novaMeta.valor_inicial);
    const valorMeta = Number(novaMeta.valor_meta);
    const dataInicio = new Date(novaMeta.periodo_inicio);
    const dataFim = new Date(novaMeta.periodo_fim);

    if (valorInicial === 0) {
      toast.error('Valor Inicial não pode ser zero');
      setLoading(false);
      return;
    }

    if (valorMeta === 0) {
      toast.error('Valor Alvo não pode ser zero');
      setLoading(false);
      return;
    }

    if (dataInicio > dataFim) {
      toast.error('Data Início não pode ser maior que a Data Fim');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const metaData = {
        ...novaMeta,
        periodo_inicio: new Date(novaMeta.periodo_inicio).toISOString(),
        periodo_fim: new Date(novaMeta.periodo_fim).toISOString(),
        valor_inicial: Number(novaMeta.valor_inicial),
        valor_meta: Number(novaMeta.valor_meta),
        peso: Number(novaMeta.peso)
      };

      await axios.post(`${API}/api/avaliacao-desempenho/metas`, metaData);
      
      toast.success('Meta criada com sucesso!');
      setNovaMeta({
        nome: '',
        descricao: '',
        indicador: '',
        unidade_medida: 'unidades',
        valor_inicial: 0,
        valor_meta: 0,
        peso: 1.0,
        tipo_meta: 'crescimento',
        periodo_inicio: '',
        periodo_fim: '',
        colaborador_id: '',
        area: ''
      });
      
      // Recarregar metas se houver colaborador selecionado
      if (novaMeta.colaborador_id) {
        await loadMetasColaborador(novaMeta.colaborador_id);
      }
    } catch (error) {
      console.error('Erro ao criar meta:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar meta');
    } finally {
      setLoading(false);
    }
  };

  const handleAtualizarResultado = async (metaId, valorObtido, comentario = '') => {
    try {
      setLoading(true);
      
      const response = await axios.put(`${API}/api/avaliacao-desempenho/metas/${metaId}/resultado`, {
        valor_obtido: Number(valorObtido),
        comentario
      });
      
      toast.success('Resultado atualizado com sucesso!');
      
      // Atualizar a lista de metas
      setMetas(metas.map(meta => 
        meta.id === metaId 
          ? { 
              ...meta, 
              ...response.data,
              valor_obtido: response.data.valor_obtido,
              percentual_atingimento: response.data.percentual_atingimento,
              classificacao: response.data.classificacao,
              score_final: response.data.score_final
            }
          : meta
      ));
      
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar resultado:', error);
      toast.error('Erro ao atualizar resultado da meta');
    } finally {
      setLoading(false);
    }
  };

  const calcularMetaSimulada = async () => {
    try {
      const response = await axios.post(`${API}/api/avaliacao-desempenho/metas/calculadora`, calculadoraMeta);
      return response.data;
    } catch (error) {
      console.error('Erro ao calcular meta:', error);
      toast.error('Erro na calculadora de metas');
    }
  };

  const loadResultadosCiclo = async (cicloId) => {
    try {
      const response = await axios.get(`${API}/api/avaliacao-desempenho/ciclos/${cicloId}/resultados`);
      setResultados(response.data.resultados || []);
    } catch (error) {
      console.error('Erro ao carregar resultados:', error);
    }
  };

  const handleCreateCiclo = async (e) => {
    e.preventDefault();
    if (!novoCiclo.nome || !novoCiclo.data_inicio || !novoCiclo.data_fim) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (novoCiclo.data_fim < novoCiclo.data_inicio) {
      toast.error('A data fim não pode ser menor que a data início');
      return;
    }
    try {
      setLoading(true);
      
      const cicloData = {
        ...novoCiclo,
        data_inicio: new Date(novoCiclo.data_inicio).toISOString(),
        data_fim: new Date(novoCiclo.data_fim).toISOString(),
        prazo_avaliacao: new Date(novoCiclo.prazo_avaliacao).toISOString(),
        prazo_aprovacao: new Date(novoCiclo.prazo_aprovacao).toISOString(),
        tipos_avaliacao: ['auto', 'gestor'] // Default
      };

      await axios.post(`${API}/api/avaliacao-desempenho/ciclos`, cicloData);
      
      toast.success('Ciclo criado com sucesso!');
      setNovoCiclo({
        nome: '',
        descricao: '',
        data_inicio: '',
        data_fim: '',
        prazo_avaliacao: '',
        prazo_aprovacao: '',
        tipos_avaliacao: [],
        peso_metas: 60,
        peso_competencias: 40,
        peso_gestor: 70,
        peso_autoavaliacao: 30
      });
      
      await loadCiclos();
    } catch (error) {
      console.error('Erro ao criar ciclo:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar ciclo');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAvaliacao = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      await axios.post(`${API}/api/avaliacao-desempenho/avaliacoes`, novaAvaliacao);
      
      toast.success('Avaliação criada com sucesso!');
      setNovaAvaliacao({
        ciclo_id: '',
        colaborador_id: '',
        tipo_avaliador: 'auto',
        respostas: [],
        pontos_fortes: '',
        areas_desenvolvimento: '',
        feedback_geral: ''
      });
      
    } catch (error) {
      console.error('Erro ao criar avaliação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const generatePDIAutomatico = async (colaboradorId, gaps) => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${API}/api/avaliacao-desempenho/gerar-pdi`, {
        colaborador_id: colaboradorId,
        gaps: gaps
      });
      
      toast.success('PDI gerado automaticamente com sucesso!');
      
      return response.data;
    } catch (error) {
      console.error('Erro ao gerar PDI:', error);
      toast.error('Erro ao gerar PDI automaticamente');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getCycleStatusBadge = (status) => {
    const statusConfig = {
      rascunho: { color: 'bg-gray-100 text-gray-800', label: 'Rascunho' },
      ativo: { color: 'bg-green-100 text-green-800', label: 'Ativo' },
      calibracao: { color: 'bg-yellow-100 text-yellow-800', label: 'Calibração' },
      concluido: { color: 'bg-blue-100 text-blue-800', label: 'Concluído' },
      cancelado: { color: 'bg-red-100 text-red-800', label: 'Cancelado' }
    };
    
    const config = statusConfig[status] || statusConfig.rascunho;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const renderMetasAvancadas = () => (
    <div className="space-y-6">
      {/* Botão Atualizar */}
      <div className="flex justify-end">
        <Button
          onClick={() => {
            if (novaMeta.colaborador_id) {
              loadMetasColaborador(novaMeta.colaborador_id);
              loadDashboardMetas(novaMeta.colaborador_id);
            }
            toast.success('Dados atualizados!');
          }}
          variant="outline"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>
      
      {/* Seletor de Colaborador */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-blue-600" />
            <span>Sistema de Metas Avançadas</span>
          </CardTitle>
          <CardDescription>
            Gerencie metas com cálculo automático de percentual de atingimento e classificação
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="colaborador_select">Selecionar Colaborador</Label>
              <Autocomplete
                value={novaMeta.colaborador_id}
                onValueChange={(value) => {
                  setNovaMeta(prev => ({...prev, colaborador_id: value}));
                  if (value) {
                    loadMetasColaborador(value);
                    loadDashboardMetas(value);
                  }
                }}
                options={colaboradores}
                getOptionValue={(colaborador) => colaborador.id}
                getOptionLabel={(colaborador) => colaborador.nome}
                placeholder="Escolha um colaborador"
                emptyMessage="Nenhum colaborador encontrado"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dashboard de Metas do Colaborador */}
      {dashboardMetas.estatisticas && (
        <Card>
          <CardHeader>
            <CardTitle>Dashboard de Metas - {dashboardMetas.colaborador_id}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Target className="h-5 w-5 text-blue-600" />
                  <span className="text-xl font-bold">{dashboardMetas.estatisticas.total_metas}</span>
                </div>
                <p className="text-sm text-gray-600">Total de Metas</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-xl font-bold">{dashboardMetas.estatisticas.metas_com_resultado}</span>
                </div>
                <p className="text-sm text-gray-600">Metas com Resultado</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Gauge className="h-5 w-5 text-purple-600" />
                  <span className="text-xl font-bold">{dashboardMetas.estatisticas.score_medio}</span>
                </div>
                <p className="text-sm text-gray-600">Score Médio</p>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5 text-orange-600" />
                  <span className="text-xl font-bold">{dashboardMetas.estatisticas.percentual_completude}%</span>
                </div>
                <p className="text-sm text-gray-600">Completude</p>
              </div>
            </div>

            {/* Distribuição por Classificação */}
            <div className="mb-4">
              <h5 className="font-medium mb-2">Distribuição por Performance</h5>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-red-50 p-3 rounded text-center">
                  <div className="text-lg font-bold text-red-600">
                    {dashboardMetas.estatisticas.distribuicao_classificacao?.nao_atingiu || 0}
                  </div>
                  <div className="text-sm text-red-700">Não Atingiu</div>
                </div>
                <div className="bg-yellow-50 p-3 rounded text-center">
                  <div className="text-lg font-bold text-yellow-600">
                    {dashboardMetas.estatisticas.distribuicao_classificacao?.atingiu || 0}
                  </div>
                  <div className="text-sm text-yellow-700">Atingiu</div>
                </div>
                <div className="bg-green-50 p-3 rounded text-center">
                  <div className="text-lg font-bold text-green-600">
                    {dashboardMetas.estatisticas.distribuicao_classificacao?.superou || 0}
                  </div>
                  <div className="text-sm text-green-700">Superou</div>
                </div>
              </div>
            </div>

            {/* Análise de IA */}
            {dashboardMetas.analise_ia && (
              <Card className="bg-purple-50 border-purple-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center space-x-2 text-lg">
                    <Brain className="h-5 w-5 text-purple-600" />
                    <span>Análise IA - Performance de Metas</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <h6 className="font-medium mb-1">Insights Principais</h6>
                      <ul className="text-sm space-y-1">
                        {dashboardMetas.analise_ia.insights?.map((insight, index) => (
                          <li key={index} className="flex items-start">
                            <span className="inline-block w-1 h-1 bg-purple-600 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                            {insight}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h6 className="font-medium mb-1">Recomendações</h6>
                      <ul className="text-sm space-y-1">
                        {dashboardMetas.analise_ia.recomendacoes?.slice(0, 3).map((rec, index) => (
                          <li key={index} className="flex items-start">
                            <ArrowRight className="h-3 w-3 text-blue-600 mt-1 mr-1 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lista de Metas */}
      {metas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Metas do Colaborador</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {metas.map(meta => (
                <Card key={meta.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold">{meta.nome}</h4>
                        <p className="text-sm text-gray-600">{meta.indicador}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={
                          meta.classificacao === 'superou' ? 'bg-green-100 text-green-800' :
                          meta.classificacao === 'atingiu' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {meta.classificacao === 'superou' ? 'Superou' :
                           meta.classificacao === 'atingiu' ? 'Atingiu' : 'Não Atingiu'}
                        </Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Inicial:</span>
                        <p>{meta.valor_inicial} {meta.unidade_medida}</p>
                      </div>
                      <div>
                        <span className="font-medium">Meta:</span>
                        <p>{meta.valor_meta} {meta.unidade_medida}</p>
                      </div>
                      <div>
                        <span className="font-medium">Obtido:</span>
                        <p>{meta.valor_obtido || 'Pendente'} {meta.valor_obtido ? meta.unidade_medida : ''}</p>
                      </div>
                      <div>
                        <span className="font-medium">Atingimento:</span>
                        <p>{meta.percentual_atingimento?.toFixed(1) || '0'}%</p>
                      </div>
                    </div>

                    {meta.percentual_atingimento && (
                      <div className="mt-3">
                        <Progress value={Math.min(meta.percentual_atingimento, 100)} className="w-full" />
                      </div>
                    )}

                    <div className="mt-3 flex space-x-2">
                      <Input
                        type="number"
                        placeholder="Resultado obtido"
                        className="flex-1"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAtualizarResultado(meta.id, e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <Button 
                        size="sm" 
                        onClick={() => {
                          const input = event.target.parentElement.querySelector('input');
                          handleAtualizarResultado(meta.id, input.value);
                          input.value = '';
                        }}
                      >
                        Atualizar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Criar Nova Meta */}
      <Card>
        <CardHeader>
          <CardTitle>Criar Nova Meta Avançada</CardTitle>
          <CardDescription>
            Defina metas com cálculo automático de percentual de atingimento
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateMeta} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome_meta">Nome da Meta</Label>
                <Input
                  id="nome_meta"
                  value={novaMeta.nome}
                  onChange={(e) => setNovaMeta(prev => ({...prev, nome: e.target.value}))}
                  placeholder="Ex: Reduzir custos em 10%"
                  required
                />
              </div>

              <div>
                <Label htmlFor="indicador">Indicador</Label>
                <Input
                  id="indicador"
                  value={novaMeta.indicador}
                  onChange={(e) => setNovaMeta(prev => ({...prev, indicador: e.target.value}))}
                  placeholder="Ex: Custo médio por entrega"
                  required
                />
              </div>

              <div>
                <Label htmlFor="tipo_meta">Tipo de Meta</Label>
                <Select 
                  value={novaMeta.tipo_meta}
                  onValueChange={(value) => setNovaMeta(prev => ({...prev, tipo_meta: value}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="crescimento">Crescimento</SelectItem>
                    <SelectItem value="reducao">Redução</SelectItem>
                    <SelectItem value="manutencao">Manutenção</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="unidade_medida">Unidade</Label>
                <Select 
                  value={novaMeta.unidade_medida}
                  onValueChange={(value) => setNovaMeta(prev => ({...prev, unidade_medida: value}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unidades">Unidades</SelectItem>
                    <SelectItem value="%">Percentual (%)</SelectItem>
                    <SelectItem value="R$">Reais (R$)</SelectItem>
                    <SelectItem value="horas">Horas</SelectItem>
                    <SelectItem value="dias">Dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="valor_inicial">Valor Inicial</Label>
                <Input
                  id="valor_inicial"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={novaMeta.valor_inicial}
                  onChange={(e) => setNovaMeta(prev => ({...prev, valor_inicial: e.target.value}))}
                  placeholder="Baseline"
                  required
                />
              </div>

              <div>
                <Label htmlFor="valor_meta">Valor Alvo</Label>
                <Input
                  id="valor_meta"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={novaMeta.valor_meta}
                  onChange={(e) => setNovaMeta(prev => ({...prev, valor_meta: e.target.value}))}
                  placeholder="Meta a atingir"
                  required
                />
              </div>

              <div>
                <Label htmlFor="periodo_inicio_meta">Início</Label>
                <Input
                  id="periodo_inicio_meta"
                  type="date"
                  value={novaMeta.periodo_inicio}
                  onChange={(e) => setNovaMeta(prev => ({...prev, periodo_inicio: e.target.value}))}
                  required
                />
              </div>

              <div>
                <Label htmlFor="periodo_fim_meta">Fim</Label>
                <Input
                  id="periodo_fim_meta"
                  type="date"
                  value={novaMeta.periodo_fim}
                  onChange={(e) => setNovaMeta(prev => ({...prev, periodo_fim: e.target.value}))}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="descricao_meta">Descrição (opcional)</Label>
              <Textarea
                id="descricao_meta"
                value={novaMeta.descricao}
                onChange={(e) => setNovaMeta(prev => ({...prev, descricao: e.target.value}))}
                placeholder="Descreva a meta em mais detalhes..."
                rows={3}
              />
            </div>

            <Button type="submit" disabled={loading}>
              {loading ? 'Criando...' : 'Criar Meta Avançada'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Avaliação de Desempenho Avançada</h1>
        <p className="text-gray-600">
          Sistema integrado de avaliação com IA para análise preditiva e geração automática de PDIs
        </p>
      </div>

      <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="metas-avancadas">Metas Avançadas</TabsTrigger>
          <TabsTrigger value="avaliacoes">Avaliações</TabsTrigger>
          <TabsTrigger value="resultados">Resultados</TabsTrigger>
        </TabsList>

        <TabsContent value="metas-avancadas">
          {renderMetasAvancadas()}
        </TabsContent>

        <TabsContent value="avaliacoes">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Avaliações Criadas</span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{avaliacoes.length} total</Badge>
                  <Button
                    size="sm"
                    onClick={() => {
                      loadAvaliacoesAvancadas(filtroStatus, filtroDepartamento);
                      toast.success('Avaliações atualizadas!');
                    }}
                    variant="outline"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Atualizar
                  </Button>
                </div>
              </CardTitle>
              <CardDescription>
                Lista completa de avaliações de desempenho avançadas criadas no sistema
              </CardDescription>
            </CardHeader>
            
            {/* Filtros */}
            <CardContent className="border-b pb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Filtrar por Status</Label>
                  <Select 
                    value={filtroStatus} 
                    onValueChange={(value) => {
                      setFiltroStatus(value);
                      loadAvaliacoesAvancadas(value, filtroDepartamento);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos os status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos os status</SelectItem>
                      <SelectItem value="em_andamento">Em Andamento</SelectItem>
                      <SelectItem value="completed">Concluídas</SelectItem>
                      <SelectItem value="atrasada">Atrasadas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Filtrar por Departamento</Label>
                  <Select 
                    value={filtroDepartamento} 
                    onValueChange={(value) => {
                      setFiltroDepartamento(value);
                      loadAvaliacoesAvancadas(filtroStatus, value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos os departamentos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos os departamentos</SelectItem>
                      {[...new Set(avaliacoes.map(a => a.colaborador_departamento).filter(Boolean))].map(departamento => (
                        <SelectItem key={departamento} value={departamento}>{departamento}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFiltroStatus('todos');
                      setFiltroDepartamento('todos');
                      loadAvaliacoesAvancadas('todos', 'todos');
                    }}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </CardContent>
            
            <CardContent>
              {avaliacoes.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhuma avaliação criada ainda</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Avaliações criadas aparecerão aqui
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {avaliacoes.map((avaliacao, index) => {
                    const statusConfig = {
                      'completed': { color: 'bg-green-100 text-green-800', label: 'Concluída', icon: CheckCircle },
                      'em_andamento': { color: 'bg-blue-100 text-blue-800', label: 'Em Andamento', icon: Clock },
                      'atrasada': { color: 'bg-red-100 text-red-800', label: 'Atrasada', icon: AlertCircle },
                      'pending': { color: 'bg-gray-100 text-gray-800', label: 'Pendente', icon: Clock },
                      'cancelled': { color: 'bg-red-100 text-red-800', label: 'Cancelada', icon: XCircle }
                    };
                    
                    const status = statusConfig[avaliacao.status] || statusConfig['pending'];
                    const StatusIcon = status.icon;
                    
                    // Classificação visual
                    const classificacaoConfig = {
                      'superou': { color: 'bg-green-500', label: 'Superou', icon: Award },
                      'atingiu': { color: 'bg-blue-500', label: 'Atingiu', icon: Target },
                      'nao_atingiu': { color: 'bg-orange-500', label: 'Não Atingiu', icon: AlertCircle },
                      'pendente': { color: 'bg-gray-400', label: 'Pendente', icon: Clock }
                    };
                    
                    const classificacao = avaliacao.metricas?.classificacao_geral || 'pendente';
                    const classConfig = classificacaoConfig[classificacao] || classificacaoConfig['pendente'];
                    const ClassIcon = classConfig.icon;
                    
                    return (
                      <Card key={avaliacao.id || index} className={`border-l-4 ${avaliacao.status === 'atrasada' || avaliacao.status === 'cancelled' ? 'border-l-red-500' : avaliacao.status === 'completed' ? 'border-l-green-500' : 'border-l-blue-500'}`}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-xl">
                                  {avaliacao.colaborador_nome || 'Colaborador não identificado'}
                                </h4>
                                <Badge className={status.color}>
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {status.label}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                                <p>
                                  <strong>Cargo:</strong> {avaliacao.colaborador_cargo || 'N/A'}
                                </p>
                                <p>
                                  <strong>Departamento:</strong> {avaliacao.colaborador_departamento || 'N/A'}
                                </p>
                                {avaliacao.periodo_inicio && avaliacao.periodo_fim && (
                                  <p className="col-span-2">
                                    <strong>Período:</strong> {new Date(avaliacao.periodo_inicio).toLocaleDateString()} - {new Date(avaliacao.periodo_fim).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Métricas de Performance */}
                            {avaliacao.metricas && avaliacao.metricas.total_metas_avaliadas > 0 && (
                              <div className="ml-4 text-center bg-gray-50 rounded-lg p-4 min-w-[180px]">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                  <ClassIcon className={`h-5 w-5 text-white rounded-full ${classConfig.color} p-1`} />
                                  <span className="font-bold text-lg">{classConfig.label}</span>
                                </div>
                                <div className="text-sm text-gray-600">
                                  <p><strong>Score Médio:</strong> {avaliacao.metricas.score_medio.toFixed(2)}</p>
                                  <p><strong>Atingimento:</strong> {avaliacao.metricas.percentual_medio}%</p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Progresso de Metas */}
                          {avaliacao.progresso && (
                            <div className="bg-blue-50 rounded-lg p-3 mb-4">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-blue-900">
                                  Progresso de Metas
                                </span>
                                <span className="text-sm font-bold text-blue-700">
                                  {avaliacao.progresso.metas_completadas}/{avaliacao.progresso.metas_totais} metas
                                </span>
                              </div>
                              <Progress 
                                value={avaliacao.progresso.percentual} 
                                className="h-2" 
                              />
                              <p className="text-xs text-blue-600 mt-1">
                                {avaliacao.progresso.percentual}% concluído
                              </p>
                            </div>
                          )}

                          {/* Ações */}
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setCurrentTab('metas-avancadas');
                                setNovaMeta(prev => ({...prev, colaborador_id: avaliacao.colaborador_id}));
                                if (avaliacao.colaborador_id) {
                                  loadMetasColaborador(avaliacao.colaborador_id);
                                  loadDashboardMetas(avaliacao.colaborador_id);
                                }
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Ver Metas
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                toast.info('Funcionalidade de exportação em desenvolvimento');
                              }}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Exportar
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => gerarAnaliseIAAvaliacao(avaliacao)}
                            >
                              <Brain className="h-4 w-4 mr-1" />
                              Análise IA
                            </Button>
                            
                            {/* Botão Concluir - só aparece se não estiver concluída nem cancelada */}
                            {avaliacao.status !== 'completed' && avaliacao.status !== 'cancelled' && (
                              <Button
                                size="sm"
                                onClick={() => concluirAvaliacao(avaliacao.id)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Concluir
                              </Button>
                            )}
                            
                            {/* Botão Cancelar - apenas admin, só aparece se não estiver concluída nem cancelada */}
                            {user?.is_admin && avaliacao.status !== 'completed' && avaliacao.status !== 'cancelled' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => cancelarAvaliacao(avaliacao.id)}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Cancelar
                              </Button>
                            )}
                          </div>
                          
                          {/* Footer com datas */}
                          <div className="mt-4 pt-3 border-t flex justify-between text-xs text-gray-500">
                            <span>Criada em: {avaliacao.created_at ? new Date(avaliacao.created_at).toLocaleDateString() : 'N/A'}</span>
                            <span>Atualizada: {avaliacao.updated_at ? new Date(avaliacao.updated_at).toLocaleDateString() : 'N/A'}</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resultados">
          <div className="space-y-6">
            {/* Cabeçalho com ação de atualizar */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Resultados e Análises</h2>
                <p className="text-gray-600">Visão consolidada de performance organizacional</p>
              </div>
              <Button 
                onClick={() => {
                  loadResultadosConsolidados();
                  loadInsightsIA();
                }}
                disabled={loadingResultados}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                {loadingResultados ? 'Atualizando...' : 'Atualizar Dados'}
              </Button>
            </div>

            {loadingResultados ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando resultados consolidados...</p>
                  </div>
                </CardContent>
              </Card>
            ) : !resultadosConsolidados ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">Nenhum dado carregado ainda</p>
                    <Button onClick={() => {
                      loadResultadosConsolidados();
                      loadInsightsIA();
                    }}>
                      Carregar Resultados
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Cards de Métricas Gerais */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Total de Avaliações</p>
                          <p className="text-3xl font-bold">{resultadosConsolidados.metricas_gerais.total_avaliacoes}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {resultadosConsolidados.metricas_gerais.total_colaboradores} colaboradores
                          </p>
                        </div>
                        <ClipboardList className="h-12 w-12 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Taxa de Conclusão</p>
                          <p className="text-3xl font-bold">{resultadosConsolidados.metricas_gerais.taxa_conclusao}%</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {resultadosConsolidados.metricas_gerais.avaliacoes_concluidas} concluídas
                          </p>
                        </div>
                        <CheckCircle className="h-12 w-12 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Score Médio Geral</p>
                          <p className="text-3xl font-bold">{resultadosConsolidados.metricas_gerais.score_medio_geral.toFixed(2)}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            De 0 a 5
                          </p>
                        </div>
                        <Star className="h-12 w-12 text-yellow-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Taxa de Conclusão de Metas</p>
                          <p className="text-3xl font-bold">{resultadosConsolidados.taxa_conclusao_metas}%</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {resultadosConsolidados.metas_com_resultado}/{resultadosConsolidados.total_metas} metas
                          </p>
                        </div>
                        <Target className="h-12 w-12 text-purple-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Gráficos */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Gráfico de Pizza - Distribuição de Performance */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Distribuição de Performance</CardTitle>
                      <CardDescription>Classificação das metas avaliadas</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Superou', value: resultadosConsolidados.distribuicao_performance.superou, color: '#10b981' },
                              { name: 'Atingiu', value: resultadosConsolidados.distribuicao_performance.atingiu, color: '#3b82f6' },
                              { name: 'Não Atingiu', value: resultadosConsolidados.distribuicao_performance.nao_atingiu, color: '#f97316' }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {[
                              { name: 'Superou', value: resultadosConsolidados.distribuicao_performance.superou, color: '#10b981' },
                              { name: 'Atingiu', value: resultadosConsolidados.distribuicao_performance.atingiu, color: '#3b82f6' },
                              { name: 'Não Atingiu', value: resultadosConsolidados.distribuicao_performance.nao_atingiu, color: '#f97316' }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      
                      {/* Legenda personalizada */}
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-2 mb-1">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span className="text-sm font-medium">Superou</span>
                          </div>
                          <p className="text-2xl font-bold">{resultadosConsolidados.distribuicao_performance.superou}</p>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-2 mb-1">
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                            <span className="text-sm font-medium">Atingiu</span>
                          </div>
                          <p className="text-2xl font-bold">{resultadosConsolidados.distribuicao_performance.atingiu}</p>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-2 mb-1">
                            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                            <span className="text-sm font-medium">Não Atingiu</span>
                          </div>
                          <p className="text-2xl font-bold">{resultadosConsolidados.distribuicao_performance.nao_atingiu}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Gráfico de Barras - Performance por Departamento */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Performance por Departamento</CardTitle>
                      <CardDescription>Score médio por departamento</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {resultadosConsolidados.analise_por_departamento && resultadosConsolidados.analise_por_departamento.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={resultadosConsolidados.analise_por_departamento}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="departamento" angle={-45} textAnchor="end" height={80} />
                            <YAxis domain={[0, 5]} />
                            <Tooltip />
                            <Bar dataKey="score_medio" fill="#3b82f6" name="Score Médio" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          Nenhum dado de departamento disponível
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Top Metas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top 5 Metas Mais Atingidas */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Award className="h-5 w-5 text-green-600" />
                        Top 5 Metas Mais Atingidas
                      </CardTitle>
                      <CardDescription>Melhores performances individuais</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {resultadosConsolidados.top_metas_atingidas && resultadosConsolidados.top_metas_atingidas.length > 0 ? (
                        <div className="space-y-3">
                          {resultadosConsolidados.top_metas_atingidas.map((meta, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                              <div className="flex-1">
                                <p className="font-semibold text-sm">{meta.nome}</p>
                                <p className="text-xs text-gray-600">{meta.colaborador}</p>
                              </div>
                              <div className="text-right">
                                <Badge className="bg-green-100 text-green-800">
                                  {meta.percentual_real.toFixed(1)}%
                                </Badge>
                                <p className="text-xs text-gray-600 mt-1">Score: {meta.score_final.toFixed(2)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-gray-500 py-8">Nenhuma meta disponível</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Top 5 Metas Mais Desafiadoras */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-orange-600" />
                        Top 5 Metas Mais Desafiadoras
                      </CardTitle>
                      <CardDescription>Metas que precisam de atenção</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {resultadosConsolidados.top_metas_desafiadoras && resultadosConsolidados.top_metas_desafiadoras.length > 0 ? (
                        <div className="space-y-3">
                          {resultadosConsolidados.top_metas_desafiadoras.map((meta, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                              <div className="flex-1">
                                <p className="font-semibold text-sm">{meta.nome}</p>
                                <p className="text-xs text-gray-600">{meta.colaborador}</p>
                              </div>
                              <div className="text-right">
                                <Badge className="bg-orange-100 text-orange-800">
                                  {meta.percentual_real.toFixed(1)}%
                                </Badge>
                                <p className="text-xs text-gray-600 mt-1">Score: {meta.score_final.toFixed(2)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-gray-500 py-8">Nenhuma meta disponível</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Insights de IA */}
                {insightsIA && insightsIA.insights_ia && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="h-5 w-5 text-purple-600" />
                        Insights Estratégicos de IA
                      </CardTitle>
                      <CardDescription>
                        Análise organizacional gerada por Inteligência Artificial
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Padrões Identificados */}
                        {insightsIA.insights_ia.padroes_identificados && insightsIA.insights_ia.padroes_identificados.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <Lightbulb className="h-4 w-4 text-yellow-600" />
                              Padrões Identificados
                            </h4>
                            <ul className="space-y-2">
                              {insightsIA.insights_ia.padroes_identificados.map((padrao, index) => (
                                <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                                  <span className="text-yellow-600 mt-1">•</span>
                                  <span>{padrao}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Pontos Fortes */}
                        {insightsIA.insights_ia.pontos_fortes_organizacionais && insightsIA.insights_ia.pontos_fortes_organizacionais.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              Pontos Fortes Organizacionais
                            </h4>
                            <ul className="space-y-2">
                              {insightsIA.insights_ia.pontos_fortes_organizacionais.map((ponto, index) => (
                                <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                                  <span className="text-green-600 mt-1">✓</span>
                                  <span>{ponto}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Áreas de Atenção */}
                        {insightsIA.insights_ia.areas_atencao && insightsIA.insights_ia.areas_atencao.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <AlertCircle className="h-4 w-4 text-orange-600" />
                              Áreas de Atenção
                            </h4>
                            <ul className="space-y-2">
                              {insightsIA.insights_ia.areas_atencao.map((area, index) => (
                                <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                                  <span className="text-orange-600 mt-1">⚠</span>
                                  <span>{area}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Recomendações Estratégicas */}
                        {insightsIA.insights_ia.recomendacoes_estrategicas && insightsIA.insights_ia.recomendacoes_estrategicas.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <ArrowRight className="h-4 w-4 text-blue-600" />
                              Recomendações Estratégicas
                            </h4>
                            <ul className="space-y-2">
                              {insightsIA.insights_ia.recomendacoes_estrategicas.map((rec, index) => (
                                <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                                  <span className="text-blue-600 mt-1">→</span>
                                  <span>{rec}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Talentos em Destaque */}
                        {insightsIA.insights_ia.talentos_destaque && insightsIA.insights_ia.talentos_destaque.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <Star className="h-4 w-4 text-yellow-600" />
                              Talentos em Destaque
                            </h4>
                            <ul className="space-y-2">
                              {insightsIA.insights_ia.talentos_destaque.map((talento, index) => (
                                <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                                  <span className="text-yellow-600 mt-1">⭐</span>
                                  <span>{talento}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Ações Prioritárias */}
                        {insightsIA.insights_ia.acoes_prioritarias && insightsIA.insights_ia.acoes_prioritarias.length > 0 && (
                          <div className="md:col-span-2">
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <Target className="h-4 w-4 text-red-600" />
                              Ações Prioritárias
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {insightsIA.insights_ia.acoes_prioritarias.map((acao, index) => (
                                <div key={index} className="p-3 bg-red-50 rounded-lg">
                                  <p className="text-sm text-gray-700 flex items-start gap-2">
                                    <span className="text-red-600 font-bold">#{index + 1}</span>
                                    <span>{acao}</span>
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Botão de Exportação */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">Exportar Relatório Completo</h4>
                        <p className="text-sm text-gray-600">Gerar relatório em PDF ou Excel com todos os dados</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={exportarPDF}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          PDF
                        </Button>
                        <Button
                          variant="outline"
                          onClick={exportarExcel}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Excel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Modal de Análise IA */}
      {showAnaliseIAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Brain className="h-6 w-6 text-purple-600" />
                    Análise de IA - Performance do Colaborador
                  </h2>
                  {analiseIAAtual && (
                    <div className="mt-2 text-sm text-gray-600">
                      <p><strong>{analiseIAAtual.colaborador}</strong> - {analiseIAAtual.cargo}</p>
                      <p>{analiseIAAtual.departamento} | Período: {analiseIAAtual.periodo}</p>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowAnaliseIAModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>

              {loadingAnaliseIA ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-12 w-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-600">Gerando análise com IA...</p>
                </div>
              ) : analiseIAAtual ? (
                <div className="space-y-6">
                  {/* Métricas Resumidas */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">{analiseIAAtual.metricas?.score_medio}</p>
                      <p className="text-sm text-gray-600">Score Médio</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{analiseIAAtual.metricas?.percentual_medio}%</p>
                      <p className="text-sm text-gray-600">Taxa de Sucesso</p>
                    </div>
                    <div className="text-center">
                      <div className={`inline-flex px-3 py-1 rounded-full text-sm font-bold ${
                        analiseIAAtual.metricas?.classificacao_geral === 'superou' ? 'bg-green-100 text-green-800' :
                        analiseIAAtual.metricas?.classificacao_geral === 'atingiu' ? 'bg-blue-100 text-blue-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {analiseIAAtual.metricas?.classificacao_geral === 'superou' ? 'Superou' :
                         analiseIAAtual.metricas?.classificacao_geral === 'atingiu' ? 'Atingiu' : 'Não Atingiu'}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Classificação</p>
                    </div>
                  </div>

                  {/* Resumo Executivo */}
                  <div className="border-l-4 border-purple-500 pl-4 bg-purple-50 p-4 rounded-r-lg">
                    <h3 className="font-semibold text-lg mb-2 text-purple-900">📋 Resumo Executivo</h3>
                    <p className="text-gray-700">{analiseIAAtual.analise.resumo_executivo}</p>
                  </div>

                  {/* Pontos Fortes */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Pontos Fortes
                    </h3>
                    <ul className="space-y-2">
                      {analiseIAAtual.analise.pontos_fortes.map((ponto, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-700">
                          <span className="text-green-600 mt-1">✓</span>
                          <span>{ponto}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Áreas de Melhoria */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-orange-600" />
                      Áreas de Melhoria
                    </h3>
                    <ul className="space-y-2">
                      {analiseIAAtual.analise.areas_melhoria.map((area, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-700">
                          <span className="text-orange-600 mt-1">⚠</span>
                          <span>{area}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Recomendações */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-yellow-600" />
                      Recomendações
                    </h3>
                    <ul className="space-y-2">
                      {analiseIAAtual.analise.recomendacoes.map((rec, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-700">
                          <span className="text-yellow-600 mt-1">💡</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Próximos Passos */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <ArrowRight className="h-5 w-5 text-blue-600" />
                      Próximos Passos
                    </h3>
                    <ol className="space-y-2">
                      {analiseIAAtual.analise.proximos_passos.map((passo, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-700">
                          <span className="text-blue-600 font-bold">{index + 1}.</span>
                          <span>{passo}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      onClick={() => setShowAnaliseIAModal(false)}
                      className="flex-1"
                    >
                      Fechar
                    </Button>
                    <Button
                      onClick={exportarAnaliseIA}
                      variant="outline"
                      className="flex-1"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Exportar Análise
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvaliacaoDesempenhoAvancada;